import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Upload,
  FileText,
  X,
  AlertCircle,
  Bookmark,
  Copy,
} from "lucide-react";
import Header from "@/components/Header";
import {
  fetchConfig,
  createApplication,
  uploadDocument,
  submitApplication,
  getApplication,
} from "@/lib/api";

const STEPS = [
  { key: "modalidad", label: "Modalidad" },
  { key: "personal", label: "Datos personales" },
  { key: "academico", label: "Datos académicos" },
  { key: "documentos", label: "Documentos" },
  { key: "revision", label: "Revisión y envío" },
];

const INITIAL_FORM = {
  modalidad: "",
  modalidad_presencial: "semipresencial",
  linea_investigacion: "",
  nombre: "",
  apellido_paterno: "",
  apellido_materno: "",
  sexo: "",
  fecha_nacimiento: "",
  nacionalidad: "Mexicana",
  curp: "",
  rfc: "",
  identificacion_numero: "",
  email: "",
  telefono: "",
  direccion: "",
  pais: "México",
  ciudad: "",
  grado_maximo: "",
  titulo_obtenido: "",
  institucion: "",
  anio_titulacion: "",
  cedula_profesional: "",
  area_conocimiento: "",
  idioma_extranjero: "Inglés",
  nivel_idioma: "",
  publicaciones: "",
  resumen_proyecto: "",
  acepta_originalidad: false,
  acepta_datos: false,
};

export default function Inscripcion() {
  const navigate = useNavigate();
  const { resumeId } = useParams();
  const [config, setConfig] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [application, setApplication] = useState(null);
  const [uploads, setUploads] = useState({}); // doc_key -> {file, progress, status, record}
  const [submitting, setSubmitting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [loadingResume, setLoadingResume] = useState(!!resumeId);
  const [showResumeModal, setShowResumeModal] = useState(false);

  useEffect(() => {
    fetchConfig().then(setConfig).catch(() => toast.error("No se pudo cargar la configuración"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resume mode: load existing application by UUID
  useEffect(() => {
    if (!resumeId) return;
    setLoadingResume(true);
    getApplication(resumeId)
      .then((app) => {
        if (app.status === "enviada") {
          toast.success(`Esta inscripción ya fue enviada · ${app.folio}`);
          navigate(`/confirmacion/${app.folio}`);
          return;
        }
        setForm({
          ...INITIAL_FORM,
          ...app,
          acepta_originalidad: false,
          acepta_datos: false,
        });
        setApplication(app);
        const ups = {};
        (app.documents || []).forEach((d) => {
          ups[d.doc_key] = {
            file: { name: d.original_filename, size: d.size },
            progress: 100,
            status: "done",
            record: d,
          };
        });
        setUploads(ups);
        setStepIdx(3); // jump to documents
        toast.success(`Reanudando inscripción ${app.folio}`);
      })
      .catch(() => {
        toast.error("No se encontró la inscripción");
        navigate("/inscripcion");
      })
      .finally(() => setLoadingResume(false));
  }, [resumeId, navigate]);

  const requiredDocs = useMemo(() => {
    if (!config) return [];
    return form.modalidad === "estancia"
      ? config.documentos_estancia
      : config.documentos_posdoctorado;
  }, [config, form.modalidad]);

  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  function validateStep(idx) {
    const e = {};
    if (idx === 0) {
      if (!form.modalidad) e.modalidad = "Selecciona una modalidad";
    }
    if (idx === 1) {
      const req = [
        ["nombre", "Nombre"],
        ["apellido_paterno", "Apellido paterno"],
        ["sexo", "Sexo"],
        ["fecha_nacimiento", "Fecha de nacimiento"],
        ["nacionalidad", "Nacionalidad"],
        ["identificacion_numero", "Número de identificación"],
        ["email", "Correo electrónico"],
        ["telefono", "Teléfono"],
        ["direccion", "Dirección"],
        ["pais", "País"],
        ["ciudad", "Ciudad"],
      ];
      req.forEach(([k, l]) => {
        if (!form[k] || String(form[k]).trim() === "") e[k] = `${l} obligatorio`;
      });
      if (form.email && !/^\S+@\S+\.\S+$/.test(form.email))
        e.email = "Correo electrónico inválido";
    }
    if (idx === 2) {
      const req = [
        ["grado_maximo", "Grado máximo"],
        ["titulo_obtenido", "Título obtenido"],
        ["institucion", "Institución"],
        ["anio_titulacion", "Año de titulación"],
        ["area_conocimiento", "Área de conocimiento"],
      ];
      req.forEach(([k, l]) => {
        if (!form[k] || String(form[k]).trim() === "") e[k] = `${l} obligatorio`;
      });
      // For posdoctorado / estancia conjunta, grado_maximo must be doctorado
      if (
        (form.modalidad === "posdoctorado" ||
          form.modalidad === "estancia_conjunta") &&
        form.grado_maximo &&
        form.grado_maximo !== "doctorado"
      ) {
        e.grado_maximo =
          "Para Posdoctorado o Estancia Conjunta se requiere Doctorado";
      }
    }
    if (idx === 4) {
      if (!form.acepta_originalidad)
        e.acepta_originalidad = "Debes aceptar la carta de originalidad";
      if (!form.acepta_datos)
        e.acepta_datos = "Debes aceptar el aviso de privacidad";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleNext() {
    if (!validateStep(stepIdx)) {
      toast.error("Revisa los campos marcados");
      return;
    }
    // When leaving step 2 (academico) and we don't have an application yet, create it
    if (stepIdx === 2 && !application) {
      setCreating(true);
      try {
        const created = await createApplication(form);
        setApplication(created);
        setShowResumeModal(true);
      } catch (err) {
        const msg = err?.response?.data?.detail || "Error al iniciar el registro";
        toast.error(msg);
        setCreating(false);
        return;
      }
      setCreating(false);
    }
    setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleBack() {
    setStepIdx((i) => Math.max(i - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleFileChange(docKey, file) {
    if (!file || !application) return;

    // Validate file type
    const isPhoto = docKey === "fotografia";
    const ext = file.name.split(".").pop().toLowerCase();
    if (isPhoto && !["jpg", "jpeg", "png"].includes(ext)) {
      toast.error("La fotografía debe ser JPG o PNG");
      return;
    }
    if (!isPhoto && ext !== "pdf") {
      toast.error("El documento debe ser PDF");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error("El archivo excede 50 MB");
      return;
    }

    setUploads((u) => ({
      ...u,
      [docKey]: { file, progress: 0, status: "uploading" },
    }));
    try {
      const record = await uploadDocument(application.id, docKey, file, (p) => {
        setUploads((u) => ({
          ...u,
          [docKey]: { ...u[docKey], progress: p },
        }));
      });
      setUploads((u) => ({
        ...u,
        [docKey]: { file, progress: 100, status: "done", record },
      }));
      toast.success("Archivo cargado");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Error al cargar el archivo";
      toast.error(msg);
      setUploads((u) => ({
        ...u,
        [docKey]: { file, progress: 0, status: "error" },
      }));
    }
  }

  async function handleSubmit() {
    if (!validateStep(4)) {
      toast.error("Acepta las condiciones");
      return;
    }
    // Verify all required docs uploaded
    const missing = requiredDocs
      .filter((d) => d.required && uploads[d.key]?.status !== "done")
      .map((d) => d.label);
    if (missing.length > 0) {
      toast.error(`Faltan documentos: ${missing.join(", ")}`);
      setStepIdx(3);
      return;
    }
    setSubmitting(true);
    try {
      const result = await submitApplication(application.id);
      toast.success("¡Inscripción enviada!");
      navigate(`/confirmacion/${result.folio}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Error al enviar la inscripción";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const resumeUrl = application
    ? `${window.location.origin}/reanudar/${application.id}`
    : "";

  if (loadingResume) {
    return (
      <div className="min-h-screen flex flex-col bg-eje/[0.02]">
        <Header variant="light" />
        <main className="flex-1 flex items-center justify-center text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando tu
          inscripción…
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-eje/[0.02]">
      <Header variant="light" />

      {showResumeModal && application && (
        <ResumeModal
          folio={application.folio}
          resumeUrl={resumeUrl}
          onClose={() => setShowResumeModal(false)}
        />
      )}

      <main className="container-narrow py-12 md:py-16 flex-1">
        <div className="mb-10">
          <div className="overline text-eje mb-2">
            Formulario de inscripción · Convocatoria 2026
          </div>
          <h1
            className="font-serif text-3xl md:text-4xl text-eje"
            data-testid="inscripcion-title"
          >
            Registro de aspirantes
          </h1>
          <p className="text-slate-600 mt-2 text-sm md:text-base max-w-2xl">
            Completa los cinco pasos y carga los documentos requeridos. Tu
            progreso se guardará una vez completes los datos académicos.
          </p>
        </div>

        {/* Resume banner (sticky, only after application is created) */}
        {application && stepIdx >= 3 && (
          <ResumeBanner folio={application.folio} resumeUrl={resumeUrl} />
        )}

        {/* Stepper */}
        <Stepper steps={STEPS} current={stepIdx} />

        <div className="card-eje p-6 md:p-10 mt-8" data-testid="form-card">
          {stepIdx === 0 && (
            <StepModalidad
              config={config}
              form={form}
              setField={setField}
              errors={errors}
            />
          )}
          {stepIdx === 1 && (
            <StepPersonal form={form} setField={setField} errors={errors} />
          )}
          {stepIdx === 2 && (
            <StepAcademico
              form={form}
              setField={setField}
              errors={errors}
            />
          )}
          {stepIdx === 3 && (
            <StepDocumentos
              requiredDocs={requiredDocs}
              uploads={uploads}
              onUpload={handleFileChange}
              applicationReady={!!application}
            />
          )}
          {stepIdx === 4 && (
            <StepRevision
              form={form}
              application={application}
              uploads={uploads}
              requiredDocs={requiredDocs}
              setField={setField}
              errors={errors}
            />
          )}

          {/* Navigation */}
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-10 mt-10 border-t border-slate-200">
            <button
              onClick={handleBack}
              disabled={stepIdx === 0}
              className="inline-flex items-center justify-center gap-2 text-eje hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed px-5 py-2.5 rounded-sm transition-colors"
              data-testid="step-back-button"
            >
              <ArrowLeft className="w-4 h-4" /> Anterior
            </button>
            {stepIdx < STEPS.length - 1 ? (
              <button
                onClick={handleNext}
                disabled={creating}
                className="btn-primary-eje inline-flex items-center justify-center gap-2"
                data-testid="step-next-button"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Guardando…
                  </>
                ) : (
                  <>
                    Siguiente <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="btn-primary-eje inline-flex items-center justify-center gap-2"
                data-testid="submit-application-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Enviando…
                  </>
                ) : (
                  <>
                    Enviar inscripción <CheckCircle2 className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

/* ---------- Stepper ---------- */
function Stepper({ steps, current }) {
  return (
    <ol className="flex items-center w-full" data-testid="stepper">
      {steps.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li
            key={s.key}
            className={`flex items-center ${
              i === steps.length - 1 ? "" : "flex-1"
            }`}
          >
            <div className="flex flex-col items-center text-center min-w-0">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-all ${
                  done
                    ? "bg-eje border-eje text-white"
                    : active
                    ? "bg-white border-eje text-eje ring-4 ring-eje/10"
                    : "bg-white border-slate-300 text-slate-400"
                }`}
                data-testid={`step-indicator-${i}`}
              >
                {done ? <CheckCircle2 className="w-5 h-5" /> : i + 1}
              </div>
              <div
                className={`text-[11px] tracking-wide mt-2 hidden sm:block ${
                  active ? "text-eje font-medium" : "text-slate-500"
                }`}
              >
                {s.label}
              </div>
            </div>
            {i < steps.length - 1 && (
              <div
                className={`flex-1 h-[2px] mx-2 sm:mx-3 ${
                  done ? "bg-eje" : "bg-slate-200"
                }`}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

/* ---------- Step 1: Modalidad ---------- */
function StepModalidad({ config, form, setField, errors }) {
  const isPosdocOrConjunta =
    form.modalidad === "posdoctorado" || form.modalidad === "estancia_conjunta";

  return (
    <div>
      <h2 className="font-serif text-2xl text-eje mb-2">
        Selecciona tu modalidad
      </h2>
      <p className="text-sm text-slate-600 mb-8">
        Esta elección define los documentos y requisitos a presentar.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {(config?.modalidades || []).map((m) => {
          const selected = form.modalidad === m.key;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => {
                setField("modalidad", m.key);
                // Reset modo a uno válido para la modalidad
                if (m.key === "estancia") {
                  setField("modalidad_presencial", "presencial");
                } else {
                  setField("modalidad_presencial", "semipresencial");
                }
              }}
              className={`text-left p-5 border-2 rounded-sm transition-all ${
                selected
                  ? "border-eje bg-eje/5 shadow-sm"
                  : "border-slate-200 hover:border-slate-400 bg-white"
              }`}
              data-testid={`modalidad-option-${m.key}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-serif text-lg text-eje">{m.label}</span>
                {selected && (
                  <CheckCircle2 className="w-5 h-5 text-eje" />
                )}
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                {m.descripcion}
              </p>
            </button>
          );
        })}
      </div>
      {errors.modalidad && <ErrorMsg msg={errors.modalidad} />}

      {isPosdocOrConjunta && (
        <div className="mb-2">
          <label className="label-eje">Modalidad de impartición</label>
          <div className="grid grid-cols-2 gap-3 max-w-md">
            {["semipresencial", "en_linea"].map((mode) => {
              const selected = form.modalidad_presencial === mode;
              return (
                <button
                  type="button"
                  key={mode}
                  onClick={() => setField("modalidad_presencial", mode)}
                  className={`px-3 py-2.5 text-sm border rounded-sm capitalize transition-colors ${
                    selected
                      ? "border-eje bg-eje text-white"
                      : "border-slate-300 text-slate-700 hover:bg-slate-50"
                  }`}
                  data-testid={`modo-${mode}`}
                >
                  {mode.replace("_", " ")}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-slate-500 mt-2">
            El Posdoctorado y la Estancia Conjunta se imparten exclusivamente en
            modalidad semipresencial o en línea.
          </p>
        </div>
      )}

      {form.modalidad === "estancia" && (
        <div className="mb-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-4 max-w-md">
          La Estancia de Investigación es presencial.
        </div>
      )}
    </div>
  );
}

/* ---------- Step 2: Datos personales ---------- */
function StepPersonal({ form, setField, errors }) {
  return (
    <div>
      <h2 className="font-serif text-2xl text-eje mb-2">Datos personales</h2>
      <p className="text-sm text-slate-600 mb-8">
        El correo electrónico será el medio principal de comunicación.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field
          label="Nombre(s) *"
          name="nombre"
          form={form}
          setField={setField}
          errors={errors}
        />
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Apellido paterno *"
            name="apellido_paterno"
            form={form}
            setField={setField}
            errors={errors}
          />
          <Field
            label="Apellido materno"
            name="apellido_materno"
            form={form}
            setField={setField}
            errors={errors}
          />
        </div>
        <Select
          label="Género *"
          name="sexo"
          form={form}
          setField={setField}
          errors={errors}
          options={[
            ["femenino", "Femenino"],
            ["masculino", "Masculino"],
            ["no_binario", "No binario"],
            ["prefiero_no_decir", "Prefiero no decir"],
          ]}
        />
        <Field
          label="Fecha de nacimiento *"
          name="fecha_nacimiento"
          type="date"
          form={form}
          setField={setField}
          errors={errors}
        />
        <Field
          label="Nacionalidad *"
          name="nacionalidad"
          form={form}
          setField={setField}
          errors={errors}
        />
        <Field
          label="Número de identificación oficial *"
          name="identificacion_numero"
          form={form}
          setField={setField}
          errors={errors}
          placeholder="Clave INE, pasaporte u otro"
        />
        <Field
          label="Correo electrónico *"
          name="email"
          type="email"
          form={form}
          setField={setField}
          errors={errors}
        />
        <Field
          label="Teléfono *"
          name="telefono"
          form={form}
          setField={setField}
          errors={errors}
          placeholder="Incluye lada"
        />
        <Field
          label="País *"
          name="pais"
          form={form}
          setField={setField}
          errors={errors}
        />
        <Field
          label="Ciudad *"
          name="ciudad"
          form={form}
          setField={setField}
          errors={errors}
        />
        <div className="md:col-span-2">
          <Field
            label="Dirección *"
            name="direccion"
            form={form}
            setField={setField}
            errors={errors}
            placeholder="Calle, número, colonia, código postal"
          />
        </div>
      </div>
    </div>
  );
}

/* ---------- Step 3: Datos académicos ---------- */
function StepAcademico({ form, setField, errors }) {
  return (
    <div>
      <h2 className="font-serif text-2xl text-eje mb-2">Datos académicos</h2>
      <p className="text-sm text-slate-600 mb-8">
        Resume tu formación y el proyecto que pretendes desarrollar.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Select
          label="Grado máximo obtenido *"
          name="grado_maximo"
          form={form}
          setField={setField}
          errors={errors}
          options={[
            ["licenciatura", "Licenciatura"],
            ["maestria", "Maestría"],
            ["doctorado", "Doctorado"],
          ]}
        />
        <Field
          label="Título obtenido *"
          name="titulo_obtenido"
          form={form}
          setField={setField}
          errors={errors}
          placeholder="Ej. Doctor en Derecho"
        />
        <Field
          label="Institución otorgante *"
          name="institucion"
          form={form}
          setField={setField}
          errors={errors}
        />
        <Field
          label="Año de titulación *"
          name="anio_titulacion"
          form={form}
          setField={setField}
          errors={errors}
          placeholder="AAAA"
        />
        <Field
          label="Cédula profesional"
          name="cedula_profesional"
          form={form}
          setField={setField}
          errors={errors}
        />
        <Field
          label="Área de conocimiento *"
          name="area_conocimiento"
          form={form}
          setField={setField}
          errors={errors}
          placeholder="Ej. Derecho Electoral, Constitucional"
        />
      </div>
    </div>
  );
}

/* ---------- Step 4: Documentos ---------- */
function StepDocumentos({ requiredDocs, uploads, onUpload, applicationReady }) {
  if (!applicationReady) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
        <p className="text-slate-600">
          Primero completa los pasos anteriores para iniciar la carga de
          documentos.
        </p>
      </div>
    );
  }
  return (
    <div>
      <h2 className="font-serif text-2xl text-eje mb-2">
        Carga de documentos
      </h2>
      <p className="text-sm text-slate-600 mb-5">
        Todos los documentos deben adjuntarse en formato PDF (fotografía en
        JPG/PNG). Tamaño máximo por archivo: 50 MB.
      </p>
      <div className="bg-eje/[0.04] border border-eje/15 rounded-sm p-4 mb-6 text-xs text-slate-700 flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="font-medium text-eje">Documentos de apoyo:</span>
        <a
          href="https://www.te.gob.mx/eje/media/pdf/6b763efb6564969.pdf"
          target="_blank"
          rel="noreferrer"
          className="text-eje underline hover:text-gold-600"
          data-testid="link-formato-registro"
        >
          Formato de registro (PDF)
        </a>
        <a
          href="https://www.te.gob.mx/eje/media/pdf/b17fe59523b4870.pdf"
          target="_blank"
          rel="noreferrer"
          className="text-eje underline hover:text-gold-600"
        >
          Criterios de originalidad (PDF)
        </a>
        <a
          href="https://www.te.gob.mx/eje/media/pdf/b43e8565dce4958.pdf"
          target="_blank"
          rel="noreferrer"
          className="text-eje underline hover:text-gold-600"
        >
          Lineamientos del proyecto académico (PDF)
        </a>
      </div>
      <ul className="space-y-3" data-testid="documents-list">
        {requiredDocs.map((doc) => {
          const upload = uploads[doc.key];
          const isPhoto = doc.key === "fotografia";
          const accept = isPhoto ? ".jpg,.jpeg,.png" : ".pdf";
          return (
            <li
              key={doc.key}
              className="border border-slate-200 bg-white rounded-sm p-4 md:p-5"
              data-testid={`doc-row-${doc.key}`}
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <div
                    className={`w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 ${
                      upload?.status === "done"
                        ? "bg-emerald-50 text-emerald-600 border border-emerald-200"
                        : "bg-slate-50 text-slate-500 border border-slate-200"
                    }`}
                  >
                    {upload?.status === "done" ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <FileText className="w-5 h-5" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800">
                      {doc.label}
                      {doc.required && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </div>
                    {upload?.file && (
                      <div className="text-xs text-slate-500 mt-0.5 truncate">
                        {upload.file.name} ·{" "}
                        {(upload.file.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    )}
                    {upload?.status === "uploading" && (
                      <div className="text-xs text-eje mt-0.5">
                        Cargando {upload.progress}%…
                      </div>
                    )}
                    {upload?.status === "error" && (
                      <div className="text-xs text-red-600 mt-0.5">
                        Error al cargar. Intenta de nuevo.
                      </div>
                    )}
                  </div>
                </div>
                <label className="cursor-pointer flex-shrink-0">
                  <input
                    type="file"
                    accept={accept}
                    className="sr-only"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) onUpload(doc.key, file);
                    }}
                    data-testid={`file-input-${doc.key}`}
                  />
                  <span
                    className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-sm border transition-colors ${
                      upload?.status === "done"
                        ? "border-eje text-eje hover:bg-eje hover:text-white"
                        : "border-eje bg-eje text-white hover:bg-eje-800"
                    }`}
                  >
                    <Upload className="w-4 h-4" />
                    {upload?.status === "done" ? "Reemplazar" : "Cargar"}
                  </span>
                </label>
              </div>
              {upload?.status === "uploading" && (
                <div className="mt-3 w-full bg-slate-100 h-1 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-eje transition-all"
                    style={{ width: `${upload.progress}%` }}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ---------- Step 5: Revisión ---------- */
function StepRevision({ form, application, uploads, requiredDocs, setField, errors }) {
  const fullName = [form.nombre, form.apellido_paterno, form.apellido_materno]
    .filter(Boolean)
    .join(" ");

  const allUploaded = requiredDocs.every(
    (d) => !d.required || uploads[d.key]?.status === "done"
  );

  return (
    <div>
      <h2 className="font-serif text-2xl text-eje mb-2">Revisión y envío</h2>
      <p className="text-sm text-slate-600 mb-8">
        Verifica que la información sea correcta. Una vez enviada, no podrá
        modificarse.
      </p>

      {application && (
        <div className="bg-eje/5 border border-eje/10 rounded-sm p-4 mb-6 flex items-center justify-between">
          <div>
            <div className="overline text-eje">Folio provisional</div>
            <div className="font-serif text-2xl text-eje">{application.folio}</div>
          </div>
          <CheckCircle2 className="w-6 h-6 text-eje" />
        </div>
      )}

      <ReviewSection title="Modalidad">
        <ReviewItem label="Modalidad" value={form.modalidad?.replace("_", " ")} />
        {(form.modalidad === "posdoctorado" ||
          form.modalidad === "estancia_conjunta") && (
          <ReviewItem
            label="Modo"
            value={form.modalidad_presencial?.replace("_", " ")}
          />
        )}
        {form.linea_investigacion && (
          <ReviewItem
            label="Línea de investigación"
            value={form.linea_investigacion?.replace(/_/g, " ")}
          />
        )}
      </ReviewSection>

      <ReviewSection title="Datos personales">
        <ReviewItem label="Nombre completo" value={fullName} />
        <ReviewItem label="Correo" value={form.email} />
        <ReviewItem label="Teléfono" value={form.telefono} />
        <ReviewItem label="Nacionalidad" value={form.nacionalidad} />
        <ReviewItem label="País / Ciudad" value={`${form.pais} · ${form.ciudad}`} />
      </ReviewSection>

      <ReviewSection title="Datos académicos">
        <ReviewItem label="Grado máximo" value={form.grado_maximo} />
        <ReviewItem label="Título" value={form.titulo_obtenido} />
        <ReviewItem label="Institución" value={form.institucion} />
        <ReviewItem label="Año" value={form.anio_titulacion} />
        <ReviewItem label="Área" value={form.area_conocimiento} />
      </ReviewSection>

      <ReviewSection title="Documentos">
        <ul className="text-sm space-y-1.5">
          {requiredDocs.map((d) => {
            const u = uploads[d.key];
            return (
              <li key={d.key} className="flex items-center gap-2">
                {u?.status === "done" ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                ) : (
                  <X className="w-4 h-4 text-red-500" />
                )}
                <span className="text-slate-700">{d.label}</span>
              </li>
            );
          })}
        </ul>
        {!allUploaded && (
          <div className="mt-3 text-xs text-red-600">
            Vuelve al paso de documentos para completar los faltantes.
          </div>
        )}
      </ReviewSection>

      <div className="space-y-3 mt-8">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.acepta_originalidad}
            onChange={(e) => setField("acepta_originalidad", e.target.checked)}
            className="mt-1 w-4 h-4 accent-eje"
            data-testid="acepta-originalidad-checkbox"
          />
          <span className="text-sm text-slate-700 leading-relaxed">
            Declaro bajo protesta de decir verdad que los trabajos y documentos
            presentados son originales, conforme a los{" "}
            <a
              href="https://www.te.gob.mx/eje/media/pdf/b17fe59523b4870.pdf"
              target="_blank"
              rel="noreferrer"
              className="text-eje underline hover:text-gold-600"
              data-testid="link-criterios-originalidad"
            >
              Criterios sobre la originalidad de los trabajos presentados a la
              EJE
            </a>
            .
          </span>
        </label>
        {errors.acepta_originalidad && (
          <ErrorMsg msg={errors.acepta_originalidad} />
        )}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.acepta_datos}
            onChange={(e) => setField("acepta_datos", e.target.checked)}
            className="mt-1 w-4 h-4 accent-eje"
            data-testid="acepta-datos-checkbox"
          />
          <span className="text-sm text-slate-700 leading-relaxed">
            He leído y acepto el{" "}
            <a
              href="/aviso-privacidad"
              target="_blank"
              rel="noreferrer"
              className="text-eje underline hover:text-gold-600"
              data-testid="link-aviso-privacidad"
            >
              Aviso de privacidad
            </a>{" "}
            de la EJE-TEPJF para el tratamiento de mis datos personales con
            fines de la presente convocatoria.
          </span>
        </label>
        {errors.acepta_datos && <ErrorMsg msg={errors.acepta_datos} />}
      </div>
    </div>
  );
}

/* ---------- Reusable ---------- */
function Field({ label, name, type = "text", form, setField, errors, placeholder }) {
  return (
    <div>
      <label className="label-eje" htmlFor={name}>
        {label}
      </label>
      <input
        id={name}
        type={type}
        className="input-eje"
        value={form[name] ?? ""}
        onChange={(e) => setField(name, e.target.value)}
        placeholder={placeholder}
        data-testid={`field-${name}`}
      />
      {errors[name] && <ErrorMsg msg={errors[name]} />}
    </div>
  );
}

function Select({ label, name, form, setField, errors, options }) {
  return (
    <div>
      <label className="label-eje" htmlFor={name}>
        {label}
      </label>
      <select
        id={name}
        className="input-eje"
        value={form[name] ?? ""}
        onChange={(e) => setField(name, e.target.value)}
        data-testid={`select-${name}`}
      >
        <option value="">— Selecciona —</option>
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
      {errors[name] && <ErrorMsg msg={errors[name]} />}
    </div>
  );
}

function ErrorMsg({ msg }) {
  return (
    <div className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
      <AlertCircle className="w-3 h-3" />
      {msg}
    </div>
  );
}

function ReviewSection({ title, children }) {
  return (
    <section className="mb-6">
      <h3 className="overline text-eje mb-3">{title}</h3>
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
        {children}
      </div>
    </section>
  );
}

function ReviewItem({ label, value }) {
  return (
    <div className="text-sm">
      <span className="text-slate-500">{label}: </span>
      <span className="text-slate-800 capitalize">{value || "—"}</span>
    </div>
  );
}

/* ---------- Resume banner & modal ---------- */
function ResumeBanner({ folio, resumeUrl }) {
  async function copy() {
    try {
      await navigator.clipboard.writeText(resumeUrl);
      toast.success("Link de reanudación copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  }
  return (
    <div
      className="mb-6 bg-gold/10 border border-gold/30 rounded-sm p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
      data-testid="resume-banner"
    >
      <div className="flex items-start gap-3">
        <Bookmark className="w-5 h-5 text-gold-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-slate-700">
          <div className="font-medium text-eje">
            Folio asignado:{" "}
            <span data-testid="banner-folio">{folio}</span>
          </div>
          <div className="mt-0.5 text-xs">
            Guarda este enlace para retomar tu inscripción si cierras esta
            ventana. No te lo enviaremos por correo.
          </div>
          <div
            className="mt-1.5 text-xs text-slate-600 break-all font-mono bg-white/60 border border-gold/20 rounded-sm px-2 py-1 inline-block max-w-full"
            data-testid="resume-url-text"
          >
            {resumeUrl}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={copy}
        className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-eje text-eje rounded-sm hover:bg-eje hover:text-white transition-colors self-start sm:self-auto flex-shrink-0"
        data-testid="copy-resume-url-banner"
      >
        <Copy className="w-3.5 h-3.5" /> Copiar enlace
      </button>
    </div>
  );
}

function ResumeModal({ folio, resumeUrl, onClose }) {
  async function copy() {
    try {
      await navigator.clipboard.writeText(resumeUrl);
      toast.success("Link copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  }
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-eje/80 backdrop-blur-sm"
      data-testid="resume-modal"
    >
      <div className="bg-white rounded-sm shadow-2xl max-w-lg w-full p-7 animate-fade-in-up">
        <div className="w-12 h-12 bg-gold/15 text-gold-600 rounded-sm flex items-center justify-center mb-4">
          <Bookmark className="w-6 h-6" />
        </div>
        <h2 className="font-serif text-2xl text-eje mb-2">
          ¡Importante! Guarda tu enlace de reanudación
        </h2>
        <p className="text-sm text-slate-600 leading-relaxed mb-5">
          Hemos asignado un folio a tu inscripción. Si cierras esta ventana
          antes de enviar el formulario, podrás retomarla únicamente con el
          enlace de abajo. No te lo enviaremos por correo.
        </p>
        <div className="bg-eje/5 border border-eje/20 rounded-sm p-4 mb-3">
          <div className="overline text-eje mb-1">Folio</div>
          <div
            className="font-serif text-3xl text-eje mb-3"
            data-testid="modal-folio"
          >
            {folio}
          </div>
          <div className="overline text-eje mb-1">Enlace de reanudación</div>
          <div
            className="text-xs text-slate-700 break-all font-mono bg-white border border-slate-200 rounded-sm px-3 py-2"
            data-testid="modal-resume-url"
          >
            {resumeUrl}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={copy}
            className="flex-1 inline-flex items-center justify-center gap-2 btn-secondary-eje"
            data-testid="modal-copy-button"
          >
            <Copy className="w-4 h-4" /> Copiar enlace
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 btn-primary-eje"
            data-testid="modal-continue-button"
          >
            Lo he guardado, continuar
          </button>
        </div>
      </div>
    </div>
  );
}
